import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import withAuth from '../../auth';
import Spinner from 'react-bootstrap/Spinner';
import NPMapViewDirections from '../google_map/NPMapViewDirections';
import { GoogleMapsProvider } from '../google_map/GoogleMapsContext';
import moment from 'moment';
import { handleApiRequest } from '../../utils/apiHandler';
import api from '../../services/axios';
import ItemSection from './pickup-create/ItemSection';
import RouteSection from './pickup-create/RouteSection';
import DeliveryOptions from './pickup-create/DeliveryOptions';
import PriceEstimate from './pickup-create/PriceEstimate';

const PickupUpdate = () => {
  const { pickupId } = useParams();
  console.log(pickupId);
  const [user, setUser] = useState(null);

  const [routes, setRoutes] = useState([
    {
      id: 1,
      origin: {},
      dropoff: {},
      originUseMyself: false,
      dropoffUseMyself: false,
    },
  ]);
  const [distance, setDistance] = useState(0);

  const [additionalInfo, setAdditionalInfo] = useState('');
  const additionalInfoRef = useRef(null);

  const [truckType, setTruckType] = useState(1);
  const [truckTypeList, setTruckTypeList] = useState([]);
  const [cargoInsurance, setCargoInsurance] = useState(6);
  const [items, setItems] = useState([]);
  const [pickup, setPickup] = useState({ origins: [], dropoffs: [] });
  const [optimizedWaypoints, setOptimizedWaypoints] = useState([]);

  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledPickupTime, setScheduledPickupTime] = useState('');

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const { hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();

  const itemIdCounter = useRef(0);
  const itemDetailsRefs = useRef({});

  const [priceEstimate, setPriceEstimate] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  // -------------------- Load pickup --------------------
  useEffect(() => {
    if (!pickupId) return;

    setIsLoading(true);

    const fetchPickups = async () => {
      await handleApiRequest({
        requestFn: () => api.get(`/manager/pickups/${pickupId}`),

        onSuccess: (response) => {
          const p = response.data;
          setUser(p.user || null);

          // Build routes from pickup.origins/dropoffs
          const originCount = p.origins?.length || 0;
          const dropoffCount = p.dropoffs?.length || 0;
          const maxRoutes = Math.max(originCount, dropoffCount, 1);

          const mappedRoutes = [];
          for (let i = 0; i < maxRoutes; i++) {
            const origin =
              i < originCount ? p.origins[i] : p.origins[originCount - 1] || {};
            const dropoff =
              i < dropoffCount
                ? p.dropoffs[i]
                : p.dropoffs[dropoffCount - 1] || {};

            mappedRoutes.push({
              id: i + 1,
              origin: {
                address: origin.address || '',
                latitude: origin.latitude || '',
                longitude: origin.longitude || '',
                contact_name: origin.contact_name || '',
                contact_phone: origin.contact_phone || '',
              },
              dropoff: {
                address: dropoff.address || '',
                latitude: dropoff.latitude || '',
                longitude: dropoff.longitude || '',
                contact_name: dropoff.contact_name || '',
                contact_phone: dropoff.contact_phone || '',
              },
              originUseMyself: false,
              dropoffUseMyself: false,
            });
          }

          setRoutes(mappedRoutes);

          // Items
          itemIdCounter.current = 0;
          const mappedItems = (p.items || []).map((it, index) => {
            const nextId = itemIdCounter.current++;
            return {
              id: nextId,
              item_name: it.item_name || '',
              weight: it.weight || '0-200Lbs',
              size: it.size || 'Small',
              details: it.details || '',
              purchase_order_number: it.purchase_order_number || '',
              routeId: index + 1, // same approach as duplicate create
              photo: null,
              photoUrl: it.photo || null, // show existing photo as preview
            };
          });

          setItems(
            mappedItems.length
              ? mappedItems
              : [
                  {
                    id: itemIdCounter.current++,
                    item_name: '',
                    weight: '0-200Lbs',
                    size: 'Small',
                    details: '',
                    purchase_order_number: '',
                    routeId: 1,
                    photo: null,
                    photoUrl: null,
                  },
                ],
          );

          setPickup({
            origins: p.origins || [],
            dropoffs: p.dropoffs || [],
          });

          setTruckType(p.truck_type?.id || 1);
          setCargoInsurance(p.cargo_insurance?.id || 6);
          setAdditionalInfo(p.additional_info || '');

          setIsScheduled(!!p.scheduled_pickup_time);
          let scheduledTime = p.scheduled_pickup_time
            ? new Date(p.scheduled_pickup_time)
            : null;
          const now = new Date();
          if (scheduledTime && scheduledTime < now) {
            scheduledTime = new Date(now.getTime() + 24 * 60 * 60 * 1000);
          }
          setScheduledPickupTime(
            scheduledTime
              ? moment(scheduledTime).format('YYYY-MM-DDTHH:mm')
              : '',
          );
        },

        onError: (message) => {
          setError(message || 'Failed to load delivery.');
        },

        finallyFn: () => {
          setIsLoading(false);
        },
      });
    };
    const getTruckTypes = async () => {
      await handleApiRequest({
        requestFn: () => api.get('/manager/truck-types'),
        onSuccess: (res) => {
          setTruckTypeList(res.data);
        },
        onError: (error) => {
          console.error('There was an error fetching the truck types!', error);
        },
      });
    };

    fetchPickups();
    getTruckTypes();
  }, [pickupId]);

  // -------------------- Helpers --------------------
  const handlePickupSelect = (id, address, location) => {
    if (!location?.lat || !location?.lng) return;

    const newRoutes = routes.map((route) =>
      route.id === id
        ? {
            ...route,
            origin: {
              ...route.origin,
              address,
              latitude: location.lat,
              longitude: location.lng,
            },
          }
        : route,
    );

    setRoutes(newRoutes);
    setPickup((prev) => ({
      ...prev,
      origins: newRoutes.map((r) => r.origin),
    }));
  };

  const handleDropoffSelect = (id, address, location) => {
    if (!location?.lat || !location?.lng) return;

    const newRoutes = routes.map((route) =>
      route.id === id
        ? {
            ...route,
            dropoff: {
              ...route.dropoff,
              address,
              latitude: location.lat,
              longitude: location.lng,
            },
          }
        : route,
    );

    setRoutes(newRoutes);
    setPickup((prev) => ({
      ...prev,
      dropoffs: newRoutes.map((r) => r.dropoff),
    }));
  };

  const handleAddRoute = () => {
    const newRouteId = routes.length ? routes[routes.length - 1].id + 1 : 1;
    setRoutes([
      ...routes,
      {
        id: newRouteId,
        origin: {},
        dropoff: {},
        originUseMyself: false,
        dropoffUseMyself: false,
      },
    ]);
  };

  const handleRemoveRoute = (id) => {
    const newRoutes = routes.filter((r) => r.id !== id);
    setRoutes(newRoutes);

    const newItems = items.filter((it) => it.routeId !== id);
    setItems(newItems);

    setPickup((prev) => ({
      ...prev,
      origins: newRoutes.map((r) => r.origin),
      dropoffs: newRoutes.map((r) => r.dropoff),
    }));
  };

  const handleRouteChange = (id, field, value, isOrigin = true) => {
    const fieldUpdates =
      field === 'address'
        ? { address: value, latitude: '', longitude: '' }
        : { [field]: value };
    const newRoutes = routes.map((route) =>
      route.id === id
        ? {
            ...route,
            [isOrigin ? 'origin' : 'dropoff']: {
              ...route[isOrigin ? 'origin' : 'dropoff'],
              ...fieldUpdates,
            },
          }
        : route,
    );

    setRoutes(newRoutes);
    setPickup((prev) => ({
      ...prev,
      origins: newRoutes.map((r) => r.origin),
      dropoffs: newRoutes.map((r) => r.dropoff),
    }));
  };

  const handleWaypointsOptimized = (waypoints) => {
    setOptimizedWaypoints(waypoints);
  };

  const handleAddItem = () => {
    if (!routes.length) return alert('Please add a route before adding items.');

    setItems([
      ...items,
      {
        id: itemIdCounter.current++,
        item_name: '',
        weight: '0-200Lbs',
        size: 'Small',
        details: '',
        purchase_order_number: '',
        routeId: routes[0].id,
        photo: null,
        photoUrl: null,
      },
    ]);
  };

  const handleUseMyselfChange = (id, isOrigin, useMyself) => {
    setRoutes((prevRoutes) =>
      prevRoutes.map((route) => {
        if (route.id !== id) return route;

        const updated = { ...route };
        if (isOrigin) {
          updated.originUseMyself = useMyself;
          if (useMyself && user) {
            updated.origin.contact_name = `${user.first_name} ${user.last_name}`;
            updated.origin.contact_phone = user.phone_num;
          } else {
            updated.origin.contact_name = '';
            updated.origin.contact_phone = '';
          }
        } else {
          updated.dropoffUseMyself = useMyself;
          if (useMyself && user) {
            updated.dropoff.contact_name = `${user.first_name} ${user.last_name}`;
            updated.dropoff.contact_phone = user.phone_num;
          } else {
            updated.dropoff.contact_name = '';
            updated.dropoff.contact_phone = '';
          }
        }
        return updated;
      }),
    );
  };

  useEffect(() => {
    if (additionalInfoRef.current) {
      additionalInfoRef.current.style.height = 'auto';
      additionalInfoRef.current.style.height = `${additionalInfoRef.current.scrollHeight}px`;
    }
  }, [additionalInfo]);

  useEffect(() => {
    items.forEach((it) => {
      const textarea = itemDetailsRefs.current[it.id];
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    });
  }, [items]);

  // -------------------- Price preview --------------------
  const lastDropoff = pickup.dropoffs[pickup.dropoffs.length - 1];
  const lastDropoffLat = lastDropoff?.latitude;
  const lastDropoffLng = lastDropoff?.longitude;

  useEffect(() => {
    if (
      distance > 0 &&
      truckType &&
      cargoInsurance &&
      lastDropoffLat &&
      lastDropoffLng
    ) {
      const requestData = {
        distance,
        truck_type_id: truckType,
        cargo_insurance_id: cargoInsurance,
        dropoff_lat: lastDropoffLat,
        dropoff_long: lastDropoffLng,
      };
      if (isScheduled && scheduledPickupTime) {
        requestData.scheduled_pickup_time = scheduledPickupTime;
      }

      setIsEstimating(true);
      setEstimateError('');

      const fetchPriceEstimate = async () => {
        await handleApiRequest({
          requestFn: () =>
            api.post('/manager/pickups/price-preview', requestData),

          onSuccess: (data) => {
            setPriceEstimate(data);
          },

          onError: (message) => {
            setEstimateError(message || 'Failed to fetch price estimate.');
          },

          finallyFn: () => {
            setIsEstimating(false);
          },
        });
      };

      fetchPriceEstimate();
    } else {
      setPriceEstimate(null);
    }
  }, [
    distance,
    truckType,
    cargoInsurance,
    lastDropoffLat,
    lastDropoffLng,
    isScheduled,
    scheduledPickupTime,
  ]);

  // -------------------- Submit update --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasPermission?.('pickup-update')) {
      setError('You do not have permission to update deliveries.');
      return;
    }

    if (isScheduled && !scheduledPickupTime) {
      setError('Please select a scheduled delivery time.');
      return;
    }
    if (isScheduled && new Date(scheduledPickupTime) < new Date()) {
      setError('Scheduled delivery time cannot be in the past.');
      return;
    }

    const itemsWithRoutes = items
      .map((it) => {
        const route = routes.find((r) => r.id === it.routeId);
        if (!route) return null;

        return {
          item_name: it.item_name,
          size: it.size,
          weight: it.weight,
          purchase_order_number: it.purchase_order_number,
          details: it.details,
          origin: {
            latitude: route.origin.latitude,
            longitude: route.origin.longitude,
            address: route.origin.address,
            contact_name: route.origin.contact_name,
            contact_phone: route.origin.contact_phone,
          },
          dropoff: {
            latitude: route.dropoff.latitude,
            longitude: route.dropoff.longitude,
            address: route.dropoff.address,
            contact_name: route.dropoff.contact_name,
            contact_phone: route.dropoff.contact_phone,
          },
          photo: it.photo,
          photoUrl: it.photoUrl,
        };
      })
      .filter(Boolean);

    const formData = new FormData();

    // Core pickup fields
    formData.append('add_info', additionalInfo || '');
    formData.append('distance', distance);

    formData.append('origin_lat', pickup.origins[0]?.latitude || '');
    formData.append('origin_long', pickup.origins[0]?.longitude || '');
    formData.append('origin_address', pickup.origins[0]?.address || '');

    formData.append(
      'dropoff_lat',
      pickup.dropoffs[pickup.dropoffs.length - 1]?.latitude || '',
    );
    formData.append(
      'dropoff_long',
      pickup.dropoffs[pickup.dropoffs.length - 1]?.longitude || '',
    );
    formData.append(
      'dropoff_address',
      pickup.dropoffs[pickup.dropoffs.length - 1]?.address || '',
    );

    formData.append('truck_type_id', truckType);
    formData.append('cargo_insurance_id', cargoInsurance);

    if (isScheduled && scheduledPickupTime) {
      formData.append('scheduled_pickup_time', scheduledPickupTime);
    }

    // Items
    itemsWithRoutes.forEach((item, index) => {
      formData.append(`items[${index}][item_name]`, item.item_name);
      formData.append(`items[${index}][weight]`, item.weight);
      formData.append(`items[${index}][size]`, item.size);
      formData.append(
        `items[${index}][purchase_order_number]`,
        item.purchase_order_number || '',
      );
      formData.append(`items[${index}][details]`, item.details || '');

      formData.append(
        `items[${index}][origin][address]`,
        item.origin.address || '',
      );
      formData.append(
        `items[${index}][origin][latitude]`,
        item.origin.latitude || '',
      );
      formData.append(
        `items[${index}][origin][longitude]`,
        item.origin.longitude || '',
      );
      formData.append(
        `items[${index}][origin][contact_name]`,
        item.origin.contact_name || '',
      );
      formData.append(
        `items[${index}][origin][contact_phone]`,
        item.origin.contact_phone || '',
      );

      formData.append(
        `items[${index}][dropoff][address]`,
        item.dropoff.address || '',
      );
      formData.append(
        `items[${index}][dropoff][latitude]`,
        item.dropoff.latitude || '',
      );
      formData.append(
        `items[${index}][dropoff][longitude]`,
        item.dropoff.longitude || '',
      );
      formData.append(
        `items[${index}][dropoff][contact_name]`,
        item.dropoff.contact_name || '',
      );
      formData.append(
        `items[${index}][dropoff][contact_phone]`,
        item.dropoff.contact_phone || '',
      );

      if (item.photo && item.photo instanceof File) {
        formData.append(`items[${index}][photo]`, item.photo);
      } else if (item.photoUrl) {
        // your admin update supports photo_url
        formData.append(`items[${index}][photo_url]`, item.photoUrl);
      }
    });

    pickup.origins.forEach((origin, index) => {
      formData.append(`origins[${index}][address]`, origin.address || '');
      formData.append(`origins[${index}][latitude]`, origin.latitude || '');
      formData.append(`origins[${index}][longitude]`, origin.longitude || '');
      formData.append(
        `origins[${index}][contact_name]`,
        origin.contact_name || '',
      );
      formData.append(
        `origins[${index}][contact_phone]`,
        origin.contact_phone || '',
      );
    });

    pickup.dropoffs.forEach((dropoff, index) => {
      formData.append(`dropoffs[${index}][address]`, dropoff.address || '');
      formData.append(`dropoffs[${index}][latitude]`, dropoff.latitude || '');
      formData.append(`dropoffs[${index}][longitude]`, dropoff.longitude || '');
      formData.append(
        `dropoffs[${index}][contact_name]`,
        dropoff.contact_name || '',
      );
      formData.append(
        `dropoffs[${index}][contact_phone]`,
        dropoff.contact_phone || '',
      );
    });

    if (optimizedWaypoints && optimizedWaypoints.length > 0) {
      optimizedWaypoints.forEach((wp, index) => {
        formData.append(
          `optimizedWaypoints[${index}][location][lat]`,
          wp.location.lat(),
        );
        formData.append(
          `optimizedWaypoints[${index}][location][lng]`,
          wp.location.lng(),
        );
      });
    }

    setError('');
    setIsSubmitted(true);

    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/pickups/${pickupId}/update`, formData),

      onSuccess: () => {
        navigate(`/pickups/${pickupId}`);
      },

      onError: (message) => {
        setError(message || 'An error occurred. Please try again later.');
      },

      finallyFn: () => {
        setIsSubmitted(false);
      },
    });
  };

  return (
    <GoogleMapsProvider>
      <div className="container-fluid mt-5">
        <h2 className="text-center">
          Edit Delivery Details <Link to={`/pickups/${pickupId}`}>#{pickupId}</Link>
          {user && (
            <>
              {' '}
              for{' '}
              <Link to={`/users/${user.id}`}>
                #{user.id} {user.company?.business_name || user.full_name}
              </Link>
            </>
          )}
        </h2>

        {error && <div className="alert alert-danger">{error}</div>}

        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="row">
            <div className="col-xl-6">
              <div className="card">
                <div className="card-body">
                  <form
                    onSubmit={handleSubmit}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        e.target.tagName !== 'TEXTAREA'
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {routes.map((route, index) => (
                      <RouteSection
                        key={route.id}
                        index={index}
                        routes={routes}
                        route={route}
                        user={user}
                        handlePickupSelect={handlePickupSelect}
                        handleDropoffSelect={handleDropoffSelect}
                        handleUseMyselfChange={handleUseMyselfChange}
                        handleRouteChange={handleRouteChange}
                        handleRemoveRoute={handleRemoveRoute}
                        handleAddRoute={handleAddRoute}
                      />
                    ))}

                    <button
                      type="button"
                      className="btn btn-secondary mb-2"
                      onClick={handleAddRoute}
                    >
                      Add Route
                    </button>

                    <DeliveryOptions
                      truckType={truckType}
                      setTruckType={setTruckType}
                      truckTypeList={truckTypeList}
                      cargoInsurance={cargoInsurance}
                      setCargoInsurance={setCargoInsurance}
                      additionalInfo={additionalInfo}
                      setAdditionalInfo={setAdditionalInfo}
                      additionalInfoRef={additionalInfoRef}
                      setIsScheduled={setIsScheduled}
                      isScheduled={isScheduled}
                      scheduledPickupTime={scheduledPickupTime}
                      setScheduledPickupTime={setScheduledPickupTime}
                    />

                    <hr />
                    <h5 className="text-center">Items</h5>

                    <ItemSection
                      items={items}
                      setItems={setItems}
                      routes={routes}
                      itemDetailsRefs={itemDetailsRefs}
                    />

                    <button
                      type="button"
                      className="btn btn-warning mb-2"
                      onClick={handleAddItem}
                    >
                      Add Item
                    </button>

                    <hr />
                    <div className="d-flex justify-content-center">
                      <button
                        className="btn btn-primary"
                        disabled={isSubmitted}
                      >
                        {isSubmitted ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            &nbsp;Updating...
                          </>
                        ) : (
                          'Update'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="card">
                <div className="card-body" style={{ minHeight: '400px' }}>
                  <NPMapViewDirections
                    origins={pickup.origins}
                    dropoffs={pickup.dropoffs}
                    onDistanceCalculated={setDistance}
                    onWaypointsOptimized={handleWaypointsOptimized}
                  />
                  <div className="mt-2">Distance: {distance} km</div>
                  <PriceEstimate
                    priceEstimate={priceEstimate}
                    estimateError={estimateError}
                    isEstimating={isEstimating}
                    truckType={truckType}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </GoogleMapsProvider>
  );
};

export default withAuth(PickupUpdate);
